import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Modal, Platform,} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AddDireccion from './AddDireccion';
import { colores, fuentes } from '../../styles/variablesCss';
import { BACKEND_URL } from '../../Config/Const';
import Storage from '../../utils/Storage';
import { useCheckoutContext } from '../../context/CheckoutContext';
// Función de utilidad para obtener datos de autenticación de manera consistente
const obtenerDatosAuth = async () => {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return {
        mail: localStorage.getItem('mail') || '',
        codUsu: localStorage.getItem('codUsu') || ''
      };
    } else {
      return {
        mail: await Storage.getItem('mail') || '',
        codUsu: await Storage.getItem('codUsu') || ''
      };
    }
  } catch (error) {
    return { mail: '', codUsu: '' };
  }
};

const Envio = forwardRef((props, ref) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [modalNuevaDireccion, setModalNuevaDireccion] = useState(false);
  const [modalEditarDireccion, setModalEditarDireccion] = useState(false);
  const [modalConfirmacion, setModalConfirmacion] = useState(false);
  const [direcciones, setDirecciones] = useState([]);
  const [direccionSeleccionada, setDireccionSeleccionada] = useState(null);
  const [direccionParaEditar, setDireccionParaEditar] = useState(null);
  const [direccionParaBorrar, setDireccionParaBorrar] = useState(null);
  const { codDic, setCodDic, setPrecioFleteDireccion, setTelefonoDireccionPrincipal } = useCheckoutContext();
  const [currentLocalidad, setCurrentLocalidad] = useState('');
  
  // Estado para controlar responsividad
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 768
  );
  const isMobile = windowWidth < 768;

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Cargar localidad actual desde almacenamiento (misma clave usada en AddDireccion)
  useEffect(() => {
    const loadLocalidad = async () => {
      try {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          setCurrentLocalidad(localStorage.getItem('ciudadNom') || '');
        } else {
          const loc = await Storage.getItem('ciudadNom');
          setCurrentLocalidad(loc || '');
        }
      } catch {
        setCurrentLocalidad('');
      }
    };
    loadLocalidad();

    // Listener para detectar cambios en localStorage (solo web)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleStorageChange = (e) => {
        if (e.key === 'ciudadNom') {
          setCurrentLocalidad(e.newValue || '');
          // Resetear codDic para que se seleccione la dirección correcta de la nueva ciudad
          setCodDic(null);
          // Recargar direcciones cuando cambia la ciudad
          cargarDireccionesUsuario();
        }
      };

      window.addEventListener('storage', handleStorageChange);
      
      // También escuchar cambios personalizados en la misma pestaña
      const handleCustomStorageChange = (e) => {
        if (e.detail?.key === 'ciudadNom') {
          setCurrentLocalidad(e.detail.newValue || '');
          // Resetear codDic para que se seleccione la dirección correcta de la nueva ciudad
          setCodDic(null);
          cargarDireccionesUsuario();
        }
      };
      
      window.addEventListener('localStorageChange', handleCustomStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('localStorageChange', handleCustomStorageChange);
      };
    }
  }, []);

  // Helper para comparación insensible a mayúsculas/acentos
  const normalizeStr = (s = '') =>
    (s || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}+/gu, '');

  // Solo mostrar direcciones que coincidan con la localidad actual (si existe)
  const filteredDirecciones = currentLocalidad
    ? direcciones.filter((d) => normalizeStr(d.localidad) === normalizeStr(currentLocalidad))
    : direcciones;

  const abrirModalDirecciones = () => {
    setModalVisible(true);
  };

  const cerrarModalDirecciones = () => {
    setModalVisible(false);
  };

  const seleccionarDireccion = (id) => {
    setCodDic(id);
    
    // Buscar la dirección seleccionada y guardar su precioFlete
    const direccion = direcciones.find(dir => dir.id === id);
    if (direccion) {
      setPrecioFleteDireccion(direccion.precioFlete || 0);
      // Guardar teléfono principal para facturación
      const telefono = direccion.celular || direccion.telefono || direccion.tel || direccion.cel || '';
      setTelefonoDireccionPrincipal(telefono || '');
    }
    
    cerrarModalDirecciones();
  };

  const abrirModalNuevaDireccion = () => {
    setModalNuevaDireccion(true);
    setModalVisible(false);
  };

  const cerrarModalNuevaDireccion = () => {
    setModalNuevaDireccion(false);
  };

  const abrirModalEditarDireccion = (direccion) => {
    // Asegurar que la estructura de datos coincide con lo que espera la API
    const direccionFormateada = {
      ...direccion,
      // Asegurarse de que todos los campos necesarios estén presentes
      codDireccion: direccion.id, // Asignar el id como codDireccion
      descripcion: direccion.referencia || '',
      nro: direccion.numero || '',
      puerta: direccion.piso || '',
      tel: direccion.telefono || '',
      cel: direccion.celular || ''
    };
    setDireccionParaEditar(direccionFormateada);
    setModalEditarDireccion(true);
    setModalVisible(false);
  };

  const cerrarModalEditarDireccion = () => {
    setModalEditarDireccion(false);
    setDireccionParaEditar(null);
  };

  const abrirModalConfirmacionBorrado = (direccion) => {
    setDireccionParaBorrar(direccion);
    setModalConfirmacion(true);
    setModalVisible(false);
  };

  const cerrarModalConfirmacion = () => {
    setModalConfirmacion(false);
    setDireccionParaBorrar(null);
  };

  // Función para cargar direcciones del usuario desde la API
  const cargarDireccionesUsuario = async () => {
   
    try {
      // Obtener datos de autenticación usando la función de utilidad
      const { mail, codUsu } = await obtenerDatosAuth();
      
      // Crear query params
      const queryParams = new URLSearchParams();
      if (mail) queryParams.append('mail', mail);
      if (codUsu) queryParams.append('codUsu', codUsu);
            
      // Realizar la llamada a la API
      const response = await fetch(`${BACKEND_URL}/rest/usuarios/direcciones?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error al obtener direcciones: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Actualizar el estado con las direcciones
      if (data && Array.isArray(data.dsResultado?.direcciones)) {
        // Adaptamos las propiedades de la API a nuestra estructura interna
        // Conservamos también las propiedades originales para la edición
        const direccionesFormateadas = data.dsResultado.direcciones.map(dir => ({
          id: dir.codDireccion,
          codDireccion: dir.codDireccion,
          calle: dir.calle,
          numero: dir.nro,
          nro: dir.nro,
          puerta: dir.puerta,
          piso: dir.puerta,
          localidad: dir.localidad,
          barrio: dir.barrio,
          telefono: dir.tel,
          tel: dir.tel,
          celular: dir.cel,
          cel: dir.cel,
          codPostal: dir.codPostal,
          codBarrio: dir.codBarrio,
          referencia: dir.descripcion,
          descripcion: dir.descripcion,
          precioFlete: dir.precioFlete || 0,
          predeterminada: dir.descripcion === 'PRINCIPAL' // Asumimos que PRINCIPAL es la dirección predeterminada
        }));
        
        setDirecciones(direccionesFormateadas);

        // Si hay direcciones, seleccionar la correcta
        if (direccionesFormateadas.length > 0) {
          // Buscar si el codDic actual existe en las nuevas direcciones
          const direccionExistente = direccionesFormateadas.find(dir => dir.id === codDic);
          
          if (direccionExistente) {
            // Si existe, usar esa dirección
            setPrecioFleteDireccion(direccionExistente.precioFlete || 0);
            const telefono = direccionExistente.celular || direccionExistente.telefono || direccionExistente.tel || direccionExistente.cel || '';
            setTelefonoDireccionPrincipal(telefono || '');
          } else {
            // Si no existe, seleccionar la predeterminada o la primera de la nueva ciudad
            const direccionPredeterminada = direccionesFormateadas.find(dir => dir.predeterminada) || direccionesFormateadas[0];
            setCodDic(direccionPredeterminada.id);
            setPrecioFleteDireccion(direccionPredeterminada.precioFlete || 0);
            const telefono = direccionPredeterminada.celular || direccionPredeterminada.telefono || direccionPredeterminada.tel || direccionPredeterminada.cel || '';
            setTelefonoDireccionPrincipal(telefono || '');
          }
        }
      } else {
        setDirecciones([]);
      }
    } catch (err) {

      setDirecciones([]);
    } finally {
    }
  };
  
  // Cargar direcciones cuando el componente se monta o cuando cambia la localidad
  useEffect(() => {
    cargarDireccionesUsuario();
  }, [currentLocalidad]);

  // Exponer funciones al padre
  useImperativeHandle(ref, () => ({
    cargarDireccionesUsuario,
  }));
  
  // Actualizar la dirección seleccionada cuando cambia el ID seleccionado o las direcciones
  useEffect(() => {
    // Buscar la dirección seleccionada entre las disponibles
    const actualizarDireccionSeleccionada = () => {
      const pool = filteredDirecciones;
      if (pool.length > 0) {
        if (codDic) {
          const direccion = pool.find(dir => dir.id === codDic);
          if (direccion) {
            // Si existe la dirección con ese codDic, usarla
            setDireccionSeleccionada(direccion);
            setPrecioFleteDireccion(direccion.precioFlete || 0);
          } else {
            // Si no existe, usar la primera y actualizar codDic
            setDireccionSeleccionada(pool[0]);
            setCodDic(pool[0].id);
            setPrecioFleteDireccion(pool[0].precioFlete || 0);
          }
        } else {
          // Si no hay ID seleccionado, usar la primera dirección y actualizar codDic
          setDireccionSeleccionada(pool[0]);
          setCodDic(pool[0].id);
          setPrecioFleteDireccion(pool[0].precioFlete || 0);
        }
      } else {
        // Si no hay direcciones en la ciudad actual, limpiar tanto la dirección como el codDic
        setDireccionSeleccionada(null);
        setCodDic(null);
      }
    };
    
    actualizarDireccionSeleccionada();
  }, [codDic, direcciones, currentLocalidad]);

  const handleSaveDireccion = (direccionCompleta, esEdicion = false) => {
    // Si es edición, actualizar la dirección existente en la lista
    if (esEdicion) {
      const nuevasDirecciones = direcciones.map(dir => 
        dir.id === direccionCompleta.id ? direccionCompleta : dir
      );
      setDirecciones(nuevasDirecciones);
      
      // Si la dirección editada es la que estaba seleccionada, actualizar la selección
      if (codDic === direccionCompleta.id) {
        setDireccionSeleccionada(direccionCompleta);
      }
      
      // Cerrar el modal de edición
      setModalEditarDireccion(false);
      setDireccionParaEditar(null);
    } else {
      // Añadir la propiedad predeterminada si es necesario
      direccionCompleta.predeterminada = direcciones.length === 0; // Si es la primera dirección, hacerla predeterminada
      
      // Añadir la nueva dirección a la lista local
      const nuevasDirecciones = [...direcciones, direccionCompleta];
      setDirecciones(nuevasDirecciones);
      
      // Seleccionar la nueva dirección
      setCodDic(direccionCompleta.id);
      
      // Cerrar el modal
      setModalNuevaDireccion(false);
      
    }
    
    // Recargar las direcciones desde la API para asegurar sincronización
    cargarDireccionesUsuario();
  };

  // Función para eliminar una dirección
  const eliminarDireccion = async () => {
    if (!direccionParaBorrar) return;
    
    try {
      // Obtener datos de autenticación usando la función de utilidad
      const { mail, codUsu } = await obtenerDatosAuth();
      
      if (!mail || !codUsu) {
        alert('Debe iniciar sesión para eliminar direcciones');
        return;
      }
      
      // Crear query params para la URL
      const queryParams = new URLSearchParams();
      queryParams.append('mail', mail);
      queryParams.append('codUsu', codUsu);
      queryParams.append('codDir', direccionParaBorrar.id || direccionParaBorrar.codDireccion);
      
      
      // Realizar la llamada DELETE a la API
      const response = await fetch(`${BACKEND_URL}/rest/usuarios/direcciones?${queryParams.toString()}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error al eliminar dirección: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Si la respuesta es exitosa
      if (data && data.dsResultado && Array.isArray(data.dsResultado.resultado) && 
          data.dsResultado.resultado[0]?.resultado === true) {
        
        // Si la dirección eliminada era la seleccionada, seleccionar otra
        if (codDic === direccionParaBorrar.id) {
          // Encontrar otra dirección para seleccionar
          const direccionesRestantes = direcciones.filter(d => d.id !== direccionParaBorrar.id);
          if (direccionesRestantes.length > 0) {
            setCodDic(direccionesRestantes[0].id);
          } else {
            setCodDic(null);
          }
        }
        
        // Actualizar la lista local
        setDirecciones(prevDirecciones => 
          prevDirecciones.filter(dir => dir.id !== direccionParaBorrar.id)
        );
        
      } else {
        const mensajeError = data?.dsResultado?.resultado[0]?.mensaje || 'Error al eliminar la dirección';
        alert(mensajeError);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      cerrarModalConfirmacion();
      // Recargar las direcciones
      cargarDireccionesUsuario();
    }
  };

  // Función para marcar una dirección como principal
  const marcarComoPrincipal = async (direccion) => {
    try {
      // Obtener datos de autenticación
      const { mail, codUsu } = await obtenerDatosAuth();
      
      if (!mail || !codUsu) {
        alert('Debe iniciar sesión para modificar direcciones');
        return;
      }
      
      // Crear el objeto de dirección para enviar
      const direccionData = {
        codDir: direccion.id || direccion.codDireccion,
        calle: direccion.calle,
        nro: direccion.numero || direccion.nro,
        piso: direccion.puerta || direccion.piso || '',
        codBarrio: direccion.codBarrio || '',
        codPostal: direccion.codPostal || '',
        tel: direccion.telefono || direccion.tel || '',
        cel: direccion.celular || direccion.cel || '',
        desDir: 'PRINCIPAL' // Marcar como PRINCIPAL
      };

      // Crear query params
      const queryParams = new URLSearchParams();
      queryParams.append('mail', mail);
      queryParams.append('codUsu', codUsu);
      queryParams.append('codDir', direccionData.codDir);
      queryParams.append('calle', direccionData.calle);
      queryParams.append('nro', direccionData.nro);
      queryParams.append('piso', direccionData.piso);
      queryParams.append('codBarrio', direccionData.codBarrio);
      queryParams.append('codPostal', direccionData.codPostal);
      queryParams.append('tel', direccionData.tel);
      queryParams.append('cel', direccionData.cel);
      queryParams.append('desDir', direccionData.desDir);
      
      // Realizar la llamada PUT a la API
      const response = await fetch(`${BACKEND_URL}/rest/usuarios/direcciones?${queryParams.toString()}`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error al marcar como principal: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Si la respuesta es exitosa
      if (data && data.dsResultado && Array.isArray(data.dsResultado.resultado) && 
          data.dsResultado.resultado[0]?.resultado === true) {
        
        // Recargar las direcciones para reflejar el cambio
        await cargarDireccionesUsuario();
        
        // Cerrar el modal si está abierto
        setModalVisible(false);
        
      } else {
        const mensajeError = data?.dsResultado?.resultado[0]?.mensaje || 'Error al marcar como principal';
        alert(mensajeError);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  // Estilos dinámicos responsivos
  const dynamicStyles = {
    container: {
      ...styles.container,
      padding: isMobile ? 10 : 16,
    },
    modalContent: {
      ...styles.modalContent,
      width: isMobile ? '95%' : '90%',
      maxWidth: isMobile ? '100%' : 600,
      maxHeight: isMobile ? '90%' : '80%',
      padding: isMobile ? 15 : 20,
    },
    modalTitulo: {
      ...styles.modalTitulo,
      fontSize: isMobile ? 16 : 18,
    },
    direccionResumen: {
      ...styles.direccionResumen,
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      paddingVertical: isMobile ? 10 : 5,
      paddingHorizontal: isMobile ? 5 : 10,
    },
    direccionInfo: {
      ...styles.direccionInfo,
      marginBottom: isMobile ? 10 : 0,
    },
    direccionCalle: {
      ...styles.direccionCalle,
      fontSize: isMobile ? 14 : 15,
    },
    cambiarDireccionBoton: {
      ...styles.cambiarDireccionBoton,
      alignSelf: isMobile ? 'flex-end' : 'auto',
    },
    direccionItem: {
      ...styles.direccionItem,
      flexDirection: isMobile ? 'column' : 'row',
      padding: isMobile ? 12 : 16,
    },
    direccionItemInfo: {
      ...styles.direccionItemInfo,
      marginLeft: isMobile ? 0 : 12,
      marginTop: isMobile ? 8 : 0,
    },
    direccionItemAcciones: {
      ...styles.direccionItemAcciones,
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginTop: isMobile ? 10 : 8,
    },
    botonesAcciones: {
      ...styles.botonesAcciones,
      marginLeft: isMobile ? 0 : 'auto',
      marginTop: isMobile ? 8 : 0,
    },
  };

  return (
    <View style={dynamicStyles.container}>      
      {/* Resumen de la dirección seleccionada */}
      <View style={dynamicStyles.direccionResumen}>
        <View style={dynamicStyles.direccionInfo}>
          <MaterialCommunityIcons name="map-marker" size={24} color={colores.rojo} style={styles.direccionIcono} />
          <View style={styles.direccionTexto}>
            <Text style={dynamicStyles.direccionCalle}>
              {direccionSeleccionada?.referencia ? `${direccionSeleccionada.referencia}: ` : ''}
              {direccionSeleccionada?.calle || 'Sin dirección seleccionada'} {direccionSeleccionada?.numero || ''}
            </Text>
            {direccionSeleccionada?.puerta && direccionSeleccionada.puerta !== '' && (
              <Text style={styles.direccionDetalles}>Puerta: {direccionSeleccionada.puerta}</Text>
            )}
            <Text style={styles.direccionDetalles}>
              {direccionSeleccionada?.localidad}{direccionSeleccionada?.barrio ? `, B° ${direccionSeleccionada.barrio}` : ''}
            </Text>
            {(direccionSeleccionada?.celular || direccionSeleccionada?.telefono) && (
              <Text style={styles.direccionReferencias}>
                {direccionSeleccionada?.celular && `Cel: ${direccionSeleccionada.celular}`}
                {direccionSeleccionada?.celular && direccionSeleccionada?.telefono && ' / '}
                {direccionSeleccionada?.telefono && `Tel: ${direccionSeleccionada.telefono}`}
              </Text>
            )}
          </View>
        </View>
        
        <TouchableOpacity style={dynamicStyles.cambiarDireccionBoton} onPress={abrirModalDirecciones}>
          <Text style={styles.cambiarDireccionTexto}>Cambiar</Text>
          <MaterialCommunityIcons name="chevron-right" size={18} color={colores.rojo} />
        </TouchableOpacity>
      </View>
      
      {/* Modal para seleccionar dirección */}
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={cerrarModalDirecciones}
      >
        <View style={styles.modalContainer}>
          <View style={dynamicStyles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={dynamicStyles.modalTitulo}>Seleccionar dirección de envío</Text>
              <TouchableOpacity onPress={cerrarModalDirecciones} style={styles.closeButton}>
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.direccionesList}>
              {filteredDirecciones.map((dir) => (
                <TouchableOpacity
                  key={dir.id}
                  style={[dynamicStyles.direccionItem, codDic === dir.id && styles.direccionSeleccionada]}
                  onPress={() => seleccionarDireccion(dir.id)}
                >
                  <View style={styles.direccionItemIcono}>
                    {codDic === dir.id ? (
                      <MaterialCommunityIcons name="radiobox-marked" size={22} color={colores.rojo} />
                    ) : (
                      <MaterialCommunityIcons name="radiobox-blank" size={22} color={colores.textoSecundario} />
                    )}
                  </View>
                  <View style={dynamicStyles.direccionItemInfo}>
                    <Text style={styles.direccionItemCalle}>
                      {dir.referencia ? `${dir.referencia}: ` : ''}
                      {dir.calle} {dir.numero}
                    </Text>
                    {dir.puerta && dir.puerta !== '' && (
                      <Text style={styles.direccionItemDetalles}>Puerta: {dir.puerta}</Text>
                    )}
                    <Text style={styles.direccionItemDetalles}>
                      {dir.localidad}{dir.barrio ? `, B° ${dir.barrio}` : ''}
                    </Text>
                    {(dir.celular || dir.telefono) && (
                      <Text style={styles.direccionItemReferencias}>
                        {dir.celular && `Cel: ${dir.celular}`}
                        {dir.celular && dir.telefono && ' / '}
                        {dir.telefono && `Tel: ${dir.telefono}`}
                      </Text>
                    )}
                    <View style={dynamicStyles.direccionItemAcciones}>
                      <View style={styles.badgesContainer}>
                        {dir.predeterminada && (
                          <View style={styles.predeterminadaBadge}>
                            <Text style={styles.predeterminadaTexto}>Predeterminada</Text>
                          </View>
                        )}
                      </View>
                      <View style={dynamicStyles.botonesAcciones}>
                        {!dir.predeterminada && (
                          <TouchableOpacity 
                            style={styles.marcarPrincipalBoton}
                            onPress={() => marcarComoPrincipal(dir)}
                          >
                            <MaterialCommunityIcons name="star-outline" size={16} color={colores.rojo} />
                            <Text style={styles.marcarPrincipalTexto}>Marcar como principal</Text>
                          </TouchableOpacity>
                        )}
                        
                        <TouchableOpacity 
                          style={styles.editarDireccionBoton}
                          onPress={() => abrirModalEditarDireccion(dir)}
                        >
                          <MaterialCommunityIcons name="pencil" size={16} color={colores.rojo} />
                          <Text style={styles.editarDireccionTexto}>Editar</Text>
                        </TouchableOpacity>
                        
                        <TouchableOpacity 
                          style={styles.eliminarDireccionBoton}
                          onPress={() => abrirModalConfirmacionBorrado(dir)}
                        >
                          <MaterialCommunityIcons name="delete-outline" size={16} color={colores.rojo} />
                          <Text style={styles.eliminarDireccionTexto}>Eliminar</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
            
            <TouchableOpacity style={styles.nuevaDireccionBoton} onPress={abrirModalNuevaDireccion}>
              <MaterialCommunityIcons name="plus" size={18} color={colores.blanco} />
              <Text style={styles.nuevaDireccionTexto}>Agregar nueva dirección</Text>
            </TouchableOpacity>
            
            {/* Se eliminó el botón Cancelar en favor del icono X */}
          </View>
        </View>
      </Modal>
      
      {/* Componente AddDireccion para agregar nueva dirección */}
      <AddDireccion 
        visible={modalNuevaDireccion}
        onClose={cerrarModalNuevaDireccion}
        onSaveDireccion={handleSaveDireccion}
      />
      
      {/* Componente AddDireccion para editar dirección */}
      <AddDireccion 
        visible={modalEditarDireccion}
        onClose={cerrarModalEditarDireccion}
        onSaveDireccion={handleSaveDireccion}
        direccionExistente={direccionParaEditar}
        modoEdicion={true}
      />

      {/* Modal de confirmación para eliminar dirección */}
      <Modal
        visible={modalConfirmacion}
        transparent={true}
        animationType="fade"
        onRequestClose={cerrarModalConfirmacion}
      >
        <View style={styles.modalContainer}>
          <View style={[styles.modalContent, styles.confirmacionModalContent]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitulo}>Eliminar dirección</Text>
              <TouchableOpacity onPress={cerrarModalConfirmacion} style={styles.closeButton}>
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
            </View>
            
            <Text style={styles.confirmacionTexto}>
              ¿Está seguro que desea eliminar esta dirección?
            </Text>
            
            <View style={styles.confirmacionBotones}>
              <TouchableOpacity style={styles.cancelarBoton} onPress={cerrarModalConfirmacion}>
                <Text style={styles.cancelarBotonTexto}>Cancelar</Text>
              </TouchableOpacity>
              
              <TouchableOpacity style={styles.eliminarBoton} onPress={eliminarDireccion}>
                <Text style={styles.eliminarBotonTexto}>Eliminar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitulo: {
    fontSize: 18,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
  },
  closeButton: {
    padding: 5,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 24,
    lineHeight: 24,
    color: colores.textoSecundario,
    textAlign: 'center',
  },
  // Estilos para el resumen de dirección seleccionada
  direccionResumen: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colores.borde,
  },
  direccionInfo: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  direccionIcono: {
    marginRight: 10,
  },
  direccionTexto: {
    flex: 1,
  },
  direccionCalle: {
    fontSize: 15,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
  },
  direccionDetalles: {
    fontSize: 13,
    fontFamily: fuentes.regular,
    color: colores.textoSecundario,
    marginTop: 2,
  },
  direccionReferencias: {
    fontSize: 13,
    fontStyle: 'italic',
    color: colores.textoSecundario,
    marginTop: 4,
  },
  cambiarDireccionBoton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
  },
  cambiarDireccionTexto: {
    fontSize: 14,
    fontFamily: fuentes.semiBold,
    color: colores.rojo,
    marginRight: 4,
  },
  // Estilos para el formulario
  formContainer: {
    marginBottom: 16,
  },
  formScroll: {
    maxHeight: 400,
    marginBottom: 10,
  },
  label: {
    fontSize: 14,
    fontFamily: fuentes.semiBold,
    color: colores.textoSecundario,
    marginBottom: 6,
  },
  input: {
    backgroundColor: colores.fondoClaro,
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colores.borde,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  halfInputContainer: {
    width: '48%',
  },
  // Estilos para el modal
  modalContainer: {
    zIndex: 999,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: colores.blanco,
    zIndex: 1000,
    padding: 20,
    paddingTop: 16,
    maxHeight: '80%',
    width: Platform.OS === 'web' ? '60%' : '90%',
    maxWidth: 600,
  },
  modalTitulo: {
    fontSize: 18,   
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
    flex: 1,
  },
  direccionesList: {
    maxHeight: 400,
  },
  direccionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colores.borde,
  },
  direccionSeleccionada: {
    backgroundColor: 'rgba(231, 29, 54, 0.05)',
  },
  direccionItemIcono: {
    marginRight: 12,
    paddingTop: 2,
  },
  direccionItemInfo: {
    flex: 1,
  },
  direccionItemAcciones: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    flexWrap: 'wrap',
  },
  badgesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  botonesAcciones: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  editarDireccionBoton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 5,
    marginRight: 10,
  },
  editarDireccionTexto: {
    fontSize: 12,
    fontFamily: fuentes.semiBold,
    color: colores.rojo,
    marginLeft: 4,
  },
  eliminarDireccionBoton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 5,
  },
  eliminarDireccionTexto: {
    fontSize: 12,
    fontFamily: fuentes.semiBold,
    color: colores.rojo,
    marginLeft: 4,
  },
  marcarPrincipalBoton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 5,
    marginRight: 10,
  },
  marcarPrincipalTexto: {
    fontSize: 12,
    fontFamily: fuentes.semiBold,
    color: colores.rojo,
    marginLeft: 4,
  },
  confirmacionModalContent: {
    maxWidth: 400,
    padding: 20,
  },
  confirmacionTexto: {
    fontSize: 16,
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal,
    marginVertical: 20,
    textAlign: 'center',
  },
  confirmacionBotones: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  cancelarBoton: {
    flex: 1,
    backgroundColor: colores.grisClaro,
    borderRadius: 8,
    paddingVertical: 12,
    marginRight: 10,
    alignItems: 'center',
  },
  cancelarBotonTexto: {
    color: colores.textoPrincipal,
    fontSize: 16,
    fontFamily: fuentes.semiBold,
  },
  eliminarBoton: {
    flex: 1,
    backgroundColor: colores.rojo,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  eliminarBotonTexto: {
    color: colores.blanco,
    fontSize: 16,
    fontFamily: fuentes.semiBold,
  },
  direccionItemCalle: {
    fontSize: 15,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
  },
  direccionItemDetalles: {
    fontSize: 13,
    fontFamily: fuentes.regular,
    color: colores.textoSecundario,
    marginTop: 2,
  },
  direccionItemReferencias: {
    fontSize: 13,
    fontStyle: 'italic',
    color: colores.textoSecundario,
    marginTop: 4,
  },
  predeterminadaBadge: {
    backgroundColor: colores.rojo,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignSelf: 'flex-start',
    marginTop: 8,
  },
  predeterminadaTexto: {
    color: colores.blanco,
    fontSize: 12,
    fontFamily: fuentes.semiBold,
  },
  nuevaDireccionBoton: {
    flexDirection: 'row',
    backgroundColor: colores.rojo,
    borderRadius: 8,
    paddingVertical: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 10,
    width: '100%',
  },
  nuevaDireccionTexto: {
    color: colores.blanco,
    fontSize: 15,
    fontFamily: fuentes.semiBold,
    marginLeft: 8,
  },
  // Los estilos del botón de guardar se movieron al componente AddDireccion
});

export default Envio;
